import includes from 'lodash/includes';
import get from 'lodash/get';

import { snakeToCamel } from 'js-utils/string';
import { createSelector } from './utils/selector';

import {
    LOCALIZED_SURVEY_QUESTIONS_BY_QUESTION_ID,
    LOCALIZED_QUESTIONS_CHOICES_BY_QUESTION_ID,
    QUESTIONS_FOR_BASIC_INFO,
    QUESTIONS_TO_OMIT_FOR_ATTENDEE_QUESTIONS,
    QUESTIONS_TO_OMIT_FOR_BUYER_QUESTIONS,
    CANNED_QUESTION_TAX_ID,
} from '../containers/checkout/constants';
import { gettext } from '../translations';
import { isCustomSurveyQuestion, isInventoryQuestion } from '../utils/surveys';

export const _shouldIncludeInAttendeeQuestions = (questionId) =>
    !includes(QUESTIONS_TO_OMIT_FOR_ATTENDEE_QUESTIONS, questionId);

export const _shouldIncludeInBuyerQuestions = (questionId) =>
    !includes(QUESTIONS_TO_OMIT_FOR_BUYER_QUESTIONS, questionId);

export const _shouldIncludeInBasicInfo = (questionId) =>
    includes(QUESTIONS_FOR_BASIC_INFO, questionId);

export const _shouldGetChoicesText = (choices, questionId) =>
    LOCALIZED_QUESTIONS_CHOICES_BY_QUESTION_ID[questionId]
        ? LOCALIZED_QUESTIONS_CHOICES_BY_QUESTION_ID[questionId]
        : choices;

const ticketNumberText = (ticketNumber) =>
    gettext('Ticket %(ticketNumber)s', { ticketNumber });

const _getAttendeeSurveyTitleText = (ticketNumber, ticketClassName) =>
    `${ticketNumberText(ticketNumber)} · ${ticketClassName}`;

const _getQuestionLabel = (question, key, questionId, taxIdName) => {
    if (questionId === CANNED_QUESTION_TAX_ID && taxIdName) {
        return taxIdName;
    }

    return isCustomSurveyQuestion(questionId)
        ? question[key]
        : LOCALIZED_SURVEY_QUESTIONS_BY_QUESTION_ID[questionId];
};

const _getChoicesText = (choices, questionId) =>
    isCustomSurveyQuestion(questionId)
        ? choices
        : _shouldGetChoicesText(choices, questionId);

export const getCountries = (state) => get(state, 'address.countries', {});

/**
 * Return all the information needed to compose an individual survey on the
 * checkout form. A survey can be:
 *     - basic info: email and first/last name only
 *         - One exception: when Prefix and/or Suffix are collected AND the event is Buyer Info survey type,
 *           basic info also includes those fields.
 *     - buyer questions: questions asked only once on the form
 *     - attendee questions: questions asked of the attendee associated with a specific ticket type
 * These are the valid combinations:
 *     - basic info only (Basic Info survey type)
 *     - basic info + buyer questions (Buyer Info survey type)
 *     - basic info + attendee questions (Each Attendee survey type)
 * @param  {string} attendeeId  (optional)  The attendee this survey belongs to. If not
 *                                          provided, the survey is for the buyer.
 * @param  {object} questions   Defines the questions, the question type, and the answer choices.
 * @param  {array}  survey      Defines which questions should be asked of the buyer
 *                              or attendee and if they're required or not.
 * @param {function} shouldIncludeQuestionHelper The helper function used to determine which questions
 *                                             should be included based on survey type.
 * @param {string} locale The locale to use for the survey.
 * @return {object}
 */
export const _buildSurvey = (
    questions,
    survey = [],
    shouldIncludeQuestionHelper,
    taxIdName,
) =>
    survey
        .filter(({ questionId }) => shouldIncludeQuestionHelper(questionId))
        .map(({ questionId, required, trigger }) => {
            const { question, type, choices, waiver } = questions[
                snakeToCamel(questionId)
            ];

            let isOutOfInventory = false;

            if (isInventoryQuestion(choices)) {
                isOutOfInventory = !choices.find(
                    ({ quantityAvailable }) => quantityAvailable > 0,
                );
            }

            return {
                choices: _getChoicesText(choices, questionId),
                required,
                type,
                name: questionId,
                text: _getQuestionLabel(
                    question,
                    'text',
                    questionId,
                    taxIdName,
                ),
                html: _getQuestionLabel(
                    question,
                    'html',
                    questionId,
                    taxIdName,
                ),
                waiverText: waiver,
                renderConditionTrigger: trigger,
                isOutOfInventory,
            };
        });

export const _buildAttendeeSurveys = (
    attendees = [],
    questions,
    shouldIncludeQuestionHelper,
) =>
    attendees.reduce(
        (attendeeSurvey, { id, survey = [], ticketClassName }, index) => {
            if (survey.length > 0) {
                attendeeSurvey.push({
                    id,
                    survey: _buildSurvey(
                        questions,
                        survey,
                        shouldIncludeQuestionHelper,
                    ),
                    ticketClassName,
                    ticketNumber: index + 1,
                });
            }

            return attendeeSurvey;
        },
        [],
    );

// Return questionsForBasicInfo
export const getQuestionsForBasicInfo = createSelector(
    (state) => get(state, 'order.questions'),
    (state) => get(state, 'order.survey'),
    (questions, survey) =>
        _buildSurvey(questions, survey, _shouldIncludeInBasicInfo),
);

// Return questionsForAttendees object
export const getQuestionsForAttendees = createSelector(
    (state) => get(state, 'order.attendees'),
    (state) => get(state, 'order.questions'),
    (attendees, questions) =>
        _buildAttendeeSurveys(
            attendees,
            questions,
            _shouldIncludeInAttendeeQuestions,
        ),
);

// Return questionsForBuyer object
export const getQuestionsForBuyer = createSelector(
    (state) => get(state, 'order.questions'),
    (state) => get(state, 'order.survey'),
    (state) => get(state, 'order.taxIdName'),
    (questions, survey, taxIdName) =>
        _buildSurvey(
            questions,
            survey,
            _shouldIncludeInBuyerQuestions,
            taxIdName,
        ),
);

export const getSurvey = (state) => ({
    questionsForAttendees: getQuestionsForAttendees(state),
    questionsForBuyer: getQuestionsForBuyer(state),
    questionsForBasicInfo: getQuestionsForBasicInfo(state),
});

export const getAttendeesSelectorOptions = createSelector(
    (state) => get(state, 'order.attendees'),
    getQuestionsForAttendees,
    (attendees, attendeeSurvey) =>
        attendeeSurvey.map(({ id, ticketClassName }) => {
            const ticketIndex = attendees.findIndex(
                ({ id: attendeeId }) => id === attendeeId,
            );

            return {
                value: id,
                display: _getAttendeeSurveyTitleText(
                    ticketIndex + 1,
                    ticketClassName,
                ),
            };
        }),
);
